import type { LockedSpotlight, DijkstraNode as Node } from '../../../types';

export const getObstacleRect = (s: LockedSpotlight) => {
  if (s.shape === 'circle') {
    return {
      x: s.x - s.radius,
      y: s.y - s.radius,
      w: s.radius * 2,
      h: s.radius * 2,
      id: s.id
    };
  } else {
    return {
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      id: s.id
    };
  }
};

export const lineIntersectsRect = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
) => {
  if (x1 >= rx && x1 <= rx + rw && y1 >= ry && y1 <= ry + rh) return true;
  if (x2 >= rx && x2 <= rx + rw && y2 >= ry && y2 <= ry + rh) return true;
  
  const intersect = (
    x1a: number,
    y1a: number,
    x2a: number,
    y2a: number,
    x1b: number,
    y1b: number,
    x2b: number,
    y2b: number
  ) => {
    const det = (x2a - x1a) * (y2b - y1b) - (y2a - y1a) * (x2b - x1b);
    if (det === 0) return false;
    const lambda = ((y2b - y1b) * (x2b - x1a) + (x1b - x2b) * (y2b - y1a)) / det;
    const gamma = ((y1a - y2a) * (x2b - x1a) + (x2a - x1a) * (y2b - y1a)) / det;
    return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1);
  };
  
  return intersect(x1, y1, x2, y2, rx, ry, rx + rw, ry) ||
         intersect(x1, y1, x2, y2, rx, ry + rh, rx + rw, ry + rh) ||
         intersect(x1, y1, x2, y2, rx, ry, rx, ry + rh) ||
         intersect(x1, y1, x2, y2, rx + rw, ry, rx + rw, ry + rh);
};

export const findRoute = (
  a: LockedSpotlight,
  b: LockedSpotlight,
  bestOption: any,
  lockedSpotlights: LockedSpotlight[],
  W: number,
  H: number,
  y_offset: number,
  iframeObstacles: Array<{ x: number, y: number, w: number, h: number, id: string }> = []
): Array<{ x: number, y: number }> => {
  const startPoint = { x: bestOption.pA.x + bestOption.pA.vx * 6, y: bestOption.pA.y + bestOption.pA.vy * 6 };
  const endPoint = { x: bestOption.pB.x + bestOption.pB.vx * 12, y: bestOption.pB.y + bestOption.pB.vy * 12 };

  // Garantir um espaço mínimo (minSpan) horizontal ou vertical para criar um arco visível quando colados
  if (bestOption.name === 'top-top' || bestOption.name === 'bottom-bottom') {
    const horizontalGap = Math.abs(endPoint.x - startPoint.x);
    const minSpan = 100;
    if (horizontalGap < minSpan) {
      const shift = (minSpan - horizontalGap) / 2;
      if (endPoint.x >= startPoint.x) {
        startPoint.x -= shift;
        endPoint.x += shift;
      } else {
        startPoint.x += shift;
        endPoint.x -= shift;
      }
    }
  } else if (bestOption.name === 'left-left' || bestOption.name === 'right-right') {
    const verticalGap = Math.abs(endPoint.y - startPoint.y);
    const minSpan = 100;
    if (verticalGap < minSpan) {
      const shift = (minSpan - verticalGap) / 2;
      if (endPoint.y >= startPoint.y) {
        startPoint.y -= shift;
        endPoint.y += shift;
      } else {
        startPoint.y += shift;
        endPoint.y -= shift;
      }
    }
  }

  // Margin of safety exit/entry
  const marginOut = 40;
  const ws = { x: startPoint.x + bestOption.pA.vx * marginOut, y: startPoint.y + bestOption.pA.vy * marginOut };
  const we = { x: endPoint.x + bestOption.pB.vx * marginOut, y: endPoint.y + bestOption.pB.vy * marginOut };

  const spotlightObstacles = lockedSpotlights.map(getObstacleRect);
  
  const rectsOverlap = (r1: { x: number, y: number, w: number, h: number }, r2: { x: number, y: number, w: number, h: number }) => {
    return Math.max(r1.x, r2.x) < Math.min(r1.x + r1.w, r2.x + r2.w) &&
           Math.max(r1.y, r2.y) < Math.min(r1.y + r1.h, r2.y + r2.h);
  };

  const obstacles = [
    ...spotlightObstacles,
    ...iframeObstacles.filter(io => {
      const rectA = getObstacleRect(a);
      const rectB = getObstacleRect(b);
      if (rectsOverlap(io, rectA) || rectsOverlap(io, rectB)) {
        return false;
      }
      return true;
    })
  ];

  const isPointInsideObstacle = (px: number, py: number, ignoreIds: string[] = []) => {
    for (const r of obstacles) {
      if (ignoreIds.includes(r.id)) continue;
      const buffer = 8;
      if (px >= r.x - buffer && px <= r.x + r.w + buffer && py >= r.y - buffer && py <= r.y + r.h + buffer) {
        return true;
      }
    }
    return false;
  };

  const isSegmentBlocked = (x1: number, y1: number, x2: number, y2: number, ignoreIds: string[] = []) => {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    
    for (const r of obstacles) {
      if (ignoreIds.includes(r.id)) continue;
      const buffer = 8;
      const ox1 = r.x - buffer;
      const ox2 = r.x + r.w + buffer;
      const oy1 = r.y - buffer;
      const oy2 = r.y + r.h + buffer;
      
      if (x1 === x2) {
        if (x1 >= ox1 && x1 <= ox2 && minY <= oy2 && maxY >= oy1) {
          return true;
        }
      } else {
        if (y1 >= oy1 && y1 <= oy2 && minX <= ox2 && maxX >= ox1) {
          return true;
        }
      }
    }
    return false;
  };

  const gridXSet = new Set<number>();
  const gridYSet = new Set<number>();

  gridXSet.add(10);
  gridXSet.add(W - 10);
  gridYSet.add(y_offset + 10);
  gridYSet.add(H + y_offset - 10);

  gridXSet.add(startPoint.x);
  gridXSet.add(ws.x);
  gridXSet.add(endPoint.x);
  gridXSet.add(we.x);

  gridYSet.add(startPoint.y);
  gridYSet.add(ws.y);
  gridYSet.add(endPoint.y);
  gridYSet.add(we.y);

  for (const r of obstacles) {
    const d = 24;
    gridXSet.add(r.x - d);
    gridXSet.add(r.x + r.w + d);
    gridYSet.add(r.y - d);
    gridYSet.add(r.y + r.h + d);
  }

  const filterX = (val: number) => Math.max(10, Math.min(W - 10, val));
  const filterY = (val: number) => Math.max(y_offset + 10, Math.min(H + y_offset - 10, val));

  const rawGridX = Array.from(gridXSet).map(filterX).sort((n1, n2) => n1 - n2);
  const rawGridY = Array.from(gridYSet).map(filterY).sort((n1, n2) => n1 - n2);

  const gridX: number[] = [];
  for (const x of rawGridX) {
    if (gridX.length === 0 || x - gridX[gridX.length - 1] > 8) {
      gridX.push(x);
    }
  }

  const gridY: number[] = [];
  for (const y of rawGridY) {
    if (gridY.length === 0 || y - gridY[gridY.length - 1] > 8) {
      gridY.push(y);
    }
  }

  const getClosest = (val: number, arr: number[]) => {
    let closest = arr[0];
    let minD = Math.abs(val - closest);
    for (const item of arr) {
      const d = Math.abs(val - item);
      if (d < minD) {
        minD = d;
        closest = item;
      }
    }
    return closest;
  };

  const ws_grid = { x: getClosest(ws.x, gridX), y: getClosest(ws.y, gridY) };
  const we_grid = { x: getClosest(we.x, gridX), y: getClosest(we.y, gridY) };

  const startKey = `${ws_grid.x},${ws_grid.y}`;
  const endKey = `${we_grid.x},${we_grid.y}`;

  const keyToNode = new Map<string, Node>();

  for (const x of gridX) {
    for (const y of gridY) {
      const key = `${x},${y}`;
      const ignore = [];
      const dStart = Math.sqrt((x - ws_grid.x) ** 2 + (y - ws_grid.y) ** 2);
      const dEnd = Math.sqrt((x - we_grid.x) ** 2 + (y - we_grid.y) ** 2);
      if (dStart < 15) ignore.push(a.id);
      if (dEnd < 15) ignore.push(b.id);

      if (!isPointInsideObstacle(x, y, ignore)) {
        keyToNode.set(key, { x, y, key });
      }
    }
  }

  if (!keyToNode.has(startKey)) {
    keyToNode.set(startKey, { x: ws_grid.x, y: ws_grid.y, key: startKey });
  }
  if (!keyToNode.has(endKey)) {
    keyToNode.set(endKey, { x: we_grid.x, y: we_grid.y, key: endKey });
  }

  const dist = new Map<string, number>();
  const parent = new Map<string, string>();
  const visited = new Set<string>();

  for (const [key] of keyToNode) {
    dist.set(key, Infinity);
  }
  dist.set(startKey, 0);

  let endReached = false;
  const allKeys = Array.from(keyToNode.keys());

  while (visited.size < allKeys.length) {
    let minD = Infinity;
    let currKey = '';
    for (const key of allKeys) {
      if (!visited.has(key)) {
        const d = dist.get(key) || Infinity;
        if (d < minD) {
          minD = d;
          currKey = key;
        }
      }
    }

    if (!currKey || minD === Infinity) break;
    if (currKey === endKey) {
      endReached = true;
      break;
    }

    visited.add(currKey);
    const currNode = keyToNode.get(currKey)!;

    const idxX = gridX.indexOf(currNode.x);
    const idxY = gridY.indexOf(currNode.y);

    const neighbors: { x: number, y: number }[] = [];
    if (idxX > 0) neighbors.push({ x: gridX[idxX - 1], y: currNode.y });
    if (idxX < gridX.length - 1) neighbors.push({ x: gridX[idxX + 1], y: currNode.y });
    if (idxY > 0) neighbors.push({ x: currNode.x, y: gridY[idxY - 1] });
    if (idxY < gridY.length - 1) neighbors.push({ x: currNode.x, y: gridY[idxY + 1] });

    for (const neigh of neighbors) {
      const neighKey = `${neigh.x},${neigh.y}`;
      if (!keyToNode.has(neighKey)) continue;

      const ignore = [];
      if (currKey === startKey || neighKey === startKey) ignore.push(a.id);
      if (currKey === endKey || neighKey === endKey) ignore.push(b.id);

      if (!isSegmentBlocked(currNode.x, currNode.y, neigh.x, neigh.y, ignore)) {
        const stepDist = Math.sqrt((neigh.x - currNode.x) ** 2 + (neigh.y - currNode.y) ** 2);
        const alt = minD + stepDist;
        const oldD = dist.get(neighKey) || Infinity;
        if (alt < oldD) {
          dist.set(neighKey, alt);
          parent.set(neighKey, currKey);
        }
      }
    }
  }

  if (endReached) {
    const routePoints: { x: number, y: number }[] = [];
    let currKey = endKey;
    while (currKey) {
      const node = keyToNode.get(currKey)!;
      routePoints.push({ x: node.x, y: node.y });
      currKey = parent.get(currKey) || '';
    }
    routePoints.reverse();
    return [startPoint, ...routePoints, endPoint];
  }

  return [startPoint, endPoint];
};

export const generateSmoothPath = (points: Array<{ x: number, y: number }>, bestOption: any) => {
  if (points.length < 2) return '';
  if (points.length === 2) {
    const pA = points[0];
    const pB = points[1];
    const dx = pB.x - pA.x;
    const dy = pB.y - pA.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const tangent_length = Math.min(150, Math.max(45, dist * 0.4));
    
    const cp1x = pA.x + bestOption.pA.vx * tangent_length;
    let cp1y = pA.y + bestOption.pA.vy * tangent_length;
    const cp2x = pB.x + bestOption.pB.vx * tangent_length;
    let cp2y = pB.y + bestOption.pB.vy * tangent_length;

    if (bestOption.pA.vx !== 0 && bestOption.pA.vy === 0 && bestOption.pB.vx !== 0 && bestOption.pB.vy === 0) {
      const horizontalGap = Math.abs(pB.x - pA.x);
      const verticalGap = Math.abs(pB.y - pA.y);
      if (horizontalGap > verticalGap) {
        const deflection = Math.max(36, Math.min(75, horizontalGap * 0.25));
        cp1y = pA.y - deflection;
        cp2y = pB.y - deflection;
      }
    }
    
    return `M ${pA.x} ${pA.y} C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${pB.x} ${pB.y}`;
  }
  
  let pathD = `M ${points[0].x} ${points[0].y}`;
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    const d1 = Math.sqrt((curr.x - prev.x) ** 2 + (curr.y - prev.y) ** 2) || 1;
    const d2 = Math.sqrt((next.x - curr.x) ** 2 + (next.y - curr.y) ** 2) || 1;
    
    const R = Math.min(30, d1 * 0.4, d2 * 0.4);
    
    const ax = curr.x - ((curr.x - prev.x) / d1) * R;
    const ay = curr.y - ((curr.y - prev.y) / d1) * R;
    
    const bx = curr.x + ((next.x - curr.x) / d2) * R;
    const by = curr.y + ((next.y - curr.y) / d2) * R;
    
    pathD += ` L ${ax} ${ay} Q ${curr.x} ${curr.y} ${bx} ${by}`;
  }
  
  pathD += ` L ${points[points.length - 1].x} ${points[points.length - 1].y}`;
  return pathD;
};
